import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { dgiDifferenceStatus, formatMoney, monthLabel } from './taxModel';

const inputClass = 'h-9 border-[#242E42] bg-[#0B0E14] text-right text-sm text-[#F3F4F6]';
const NUMBER = /^\d+(\.\d{1,2})?$/;
const FIELDS = [
  ['sales_base', 'Ventas (base)'],
  ['credit_notes_base', 'Notas de crédito (base)'],
  ['itbms', 'ITBMS'],
];

const toDraft = (control) => Object.fromEntries(control.months.map((month) => [month.month, {
  sales_base: month.entered ? month.sales_base : '', credit_notes_base: month.entered ? month.credit_notes_base : '',
  itbms: month.entered ? month.itbms : '', voucher: month.voucher || '',
}]));

// Monthly "Informe de ventas" the DGI sends (Form. 1091-0): compared against the ERP invoices.
export function DgiControlPanel({ report, saving, onSave }) {
  const control = report.dgi_control;
  const locked = report.frozen;
  const [draft, setDraft] = useState(() => toDraft(control));
  const [useDgi, setUseDgi] = useState(control.use_dgi);
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(toDraft(control));
    setUseDgi(control.use_dgi);
    setError('');
  }, [control]);

  const change = (month, field, value) => setDraft((prev) => ({ ...prev, [month]: { ...prev[month], [field]: value } }));

  const submit = (event) => {
    event.preventDefault();
    const months = {};
    for (const [month, entry] of Object.entries(draft)) {
      const values = FIELDS.map(([field]) => String(entry[field] || '').trim());
      if (values.every((value) => !value) && !entry.voucher.trim()) continue;
      const bad = FIELDS.find(([field], index) => values[index] && !NUMBER.test(values[index]));
      if (bad) { setError(`${monthLabel(month)}: «${bad[1]}» no es un monto válido (números positivos con hasta 2 decimales).`); return; }
      months[month] = { sales_base: values[0] || '0', credit_notes_base: values[1] || '0', itbms: values[2] || '0', voucher: entry.voucher.trim() };
    }
    if (useDgi && Object.keys(months).length !== control.months.length) {
      setError('Para usar las cifras de la DGI registra todos los meses del período.');
      return;
    }
    setError('');
    onSave({ months, use_dgi: useDgi });
  };

  return (
    <section className="no-print rounded-lg border border-[#242E42] bg-[#151B26] p-4" aria-label="Control con los informes de ventas de la DGI" data-testid="dgi-control">
      <h3 className="font-['Outfit'] text-base font-semibold text-[#F3F4F6]">Control con los informes de la DGI</h3>
      <p className="mt-1 text-xs text-[#9CA3AF]">
        Cada mes la DGI envía el «Informe de ventas» con lo facturado electrónicamente. Escribe aquí sus cifras para compararlas con las facturas del ERP.
        Si el ERP no tiene todas las facturas, activa «Usar las cifras de la DGI» y el formulario toma las ventas de la DGI.
      </p>

      <form onSubmit={submit} className="mt-3 space-y-3">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#9CA3AF]">
                <th className="px-2 py-1.5">Mes</th>
                {FIELDS.map(([field, label]) => <th key={field} className="px-2 py-1.5 text-right">{label}</th>)}
                <th className="px-2 py-1.5">N.° comprobante</th>
                <th className="px-2 py-1.5 text-right">Ventas en el ERP</th>
                <th className="px-2 py-1.5">Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {control.months.map((month) => {
                const status = dgiDifferenceStatus(month);
                return (
                  <tr key={month.month} className="border-t border-[#242E42]">
                    <td className="whitespace-nowrap px-2 py-1.5 capitalize text-[#D1D5DB]">{monthLabel(month.month)}</td>
                    {FIELDS.map(([field, label]) => (
                      <td key={field} className="px-2 py-1.5">
                        <Input
                          aria-label={`${label} ${monthLabel(month.month)}`}
                          inputMode="decimal"
                          disabled={locked}
                          value={draft[month.month]?.[field] ?? ''}
                          onChange={(event) => change(month.month, field, event.target.value)}
                          className={`w-28 ${inputClass}`}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      <Input
                        aria-label={`Número de comprobante ${monthLabel(month.month)}`}
                        disabled={locked}
                        maxLength={30}
                        value={draft[month.month]?.voucher ?? ''}
                        onChange={(event) => change(month.month, 'voucher', event.target.value)}
                        className={`w-36 text-left ${inputClass}`}
                      />
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-[#D1D5DB]">{formatMoney(month.erp_base)}</td>
                    <td className={`min-w-[10rem] px-2 py-1.5 text-xs font-medium ${status.tone}`}>{status.text}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#242E42] font-semibold text-[#F3F4F6]">
                <td className="px-2 py-1.5">Total</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatMoney(control.dgi_sales_base)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatMoney(control.credit_notes_base)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatMoney(control.dgi_itbms)}</td>
                <td />
                <td className="px-2 py-1.5 text-right tabular-nums">{formatMoney(control.erp_sales_base)}</td>
                <td className="px-2 py-1.5 text-xs">{control.complete ? `B/. ${formatMoney(control.difference)}` : ''}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <label className="flex items-start gap-2 text-sm text-[#D1D5DB]">
          <input type="checkbox" className="mt-1" checked={useDgi} disabled={locked} onChange={(event) => setUseDgi(event.target.checked)} />
          <span>
            <strong>Usar las cifras de la DGI</strong> en el formulario (casillas 11 y 402/403). La retención (casilla 52) sigue saliendo solo de las facturas del ERP marcadas con retención.
          </span>
        </label>

        {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
        {!locked && (
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">{saving ? 'Guardando…' : 'Guardar control DGI'}</Button>
          </div>
        )}
      </form>
    </section>
  );
}
